// hooks/useEvents.js
import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/api';

const PAGE_SIZE = 50;

const useEvents = (user) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Fonction pour fetcher les événements (page initiale)
  const fetchEvents = useCallback(async (forceRefresh = false) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (!user) {
        setEvents([]);
        setLoading(false);
        return;
      }

      if (forceRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);

      const response = await api.get('/events', {
        params: { limit: PAGE_SIZE, skip: 0, withCount: true },
        signal: abortControllerRef.current.signal
      });

      if (!isMountedRef.current) return;

      const data = response.data || {};
      const eventsList = data.events || (Array.isArray(data) ? data : []);
      const total = data.total || eventsList.length;

      setEvents(eventsList);
      setHasMore(eventsList.length < total);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!isMountedRef.current) return;
      console.error('[useEvents] Error fetching events:', err);
      setError(err.message);
      setEvents([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [user]);

  // Charger plus d'événements
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    try {
      const controller = new AbortController();
      const response = await api.get('/events', {
        params: { limit: PAGE_SIZE, skip: events.length, withCount: true },
        signal: controller.signal
      });

      if (!isMountedRef.current) return;

      const data = response.data;
      const newEvents = data.events || data;
      const total = data.total || 0;

      setEvents(prev => {
        // Dédoublonner (les saisonniers peuvent être en double)
        const existingIds = new Set(prev.map(e => e.id));
        const unique = newEvents.filter(e => !existingIds.has(e.id));
        const merged = [...prev, ...unique];
        setHasMore(merged.length < total);
        return merged;
      });
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      console.error('[useEvents] Error loading more:', err);
    } finally {
      if (isMountedRef.current) {
        setLoadingMore(false);
      }
    }
  }, [events.length, hasMore, loadingMore]);

  // Fetch initial
  useEffect(() => {
    isMountedRef.current = true;
    fetchEvents();

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchEvents]);

  const refetch = useCallback(() => fetchEvents(true), [fetchEvents]);
  const refresh = useCallback(() => fetchEvents(true), [fetchEvents]);

  const updateEventInState = useCallback((eventId, updates) => {
    setEvents(prevEvents =>
      prevEvents.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      )
    );
  }, []);

  return {
    events,
    loading,
    error,
    isRefreshing,
    hasMore,
    loadingMore,
    loadMore,
    refetch,
    refresh,
    updateEventInState
  };
};

export default useEvents;
